import {
  Box,
  Card,
  CardContent,
  Collapse,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { EmptyState, ErrorBanner, InlineSpinner, isoDate } from '@/components/common'

/** One kind of document, and how often it left the building. */
type PrintedKind = {
  documentType: string
  printCount: number
  downloadCount: number
  totalCount: number
  documentCount: number
  lastPrintedAt: string | null
}

/** One printing of one document. */
type PrintedCopy = {
  documentType: string
  documentId: string | null
  documentNumber: string | null
  printedBy: string
  how: string
  printedAt: string
  from: string | null
}

const when = (value: string | null) =>
  value ? new Date(value).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—'

/**
 * The names and the times behind one count.
 *
 * <p>Fetched only once the count is opened. A page that reads every printing of
 * every kind of document to show six numbers is a page that gets slower every
 * month it is used.</p>
 */
function WhoPrintedIt({ documentType, from, to }: { documentType: string; from: string; to: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['documents-printed-detail', documentType, from, to] as const,
    queryFn: () =>
      api.get<PrintedCopy[]>('/api/v1/reports/documents-printed/detail', {
        documentType,
        from,
        to,
      }),
  })

  if (isLoading) return <InlineSpinner />
  if (error) return <ErrorBanner error={error} />
  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        Nothing in this period.
      </Typography>
    )
  }

  return (
    <Box sx={{ py: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Document no.</TableCell>
            <TableCell>Taken by</TableCell>
            <TableCell>How</TableCell>
            <TableCell>At</TableCell>
            <TableCell>From</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((copy, index) => (
            <TableRow key={`${copy.printedAt}-${index}`}>
              {/* A blank diary page carries no number, and neither does the
                  row for it. */}
              <TableCell>{copy.documentNumber ?? '—'}</TableCell>
              <TableCell>{copy.printedBy}</TableCell>
              <TableCell>{copy.how}</TableCell>
              <TableCell>{when(copy.printedAt)}</TableCell>
              <TableCell>{copy.from ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {data.length === 500 ? (
        <Typography variant="caption" color="text.secondary">
          The latest 500. Narrow the dates to see further back.
        </Typography>
      ) : null}
    </Box>
  )
}

/**
 * What the office printed, by kind of document.
 *
 * <p>A document handed to a customer is something the business has parted
 * with. A quotation reprinted six times the day before an event, or a bill
 * printed after hours by somebody who does not raise bills, is worth being
 * able to look at - so the count opens into the names and the times behind
 * it.</p>
 *
 * <p>Printed and downloaded are counted apart, because sending a bill to the
 * printer and keeping the file are not the same act. The count that opens is
 * the two together, and the document count is beside it: forty prints of two
 * quotations is not forty quotations printed once.</p>
 */
export function DocumentsPrintedCard() {
  const [from, setFrom] = useState(() => isoDate(-30))
  const [to, setTo] = useState(() => isoDate(0))
  const [open, setOpen] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['documents-printed', from, to] as const,
    queryFn: () => api.get<PrintedKind[]>('/api/v1/reports/documents-printed', { from, to }),
  })

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
          <TextField
            label="From"
            type="date"
            size="small"
            value={from}
            onChange={(event) => setFrom(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Stack>

        <ErrorBanner error={error} />

        {isLoading ? (
          <InlineSpinner />
        ) : !data || data.length === 0 ? (
          <EmptyState
            message="Nothing printed in this period"
            hint="Every document taken off the system as a PDF is counted here."
          />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Document type</TableCell>
                <TableCell align="right">Printed</TableCell>
                <TableCell align="right">Downloaded</TableCell>
                <TableCell align="right">Print count</TableCell>
                <TableCell align="right">Documents</TableCell>
                <TableCell>Last taken</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((kind) => (
                <Fragment key={kind.documentType}>
                  <TableRow hover>
                    <TableCell>{kind.documentType}</TableCell>
                    <TableCell align="right">{kind.printCount}</TableCell>
                    <TableCell align="right">{kind.downloadCount}</TableCell>
                    <TableCell align="right">
                      {/* The count is the way in: what an office wants after
                          seeing it is who, how and when. */}
                      <Link
                        component="button"
                        type="button"
                        underline="hover"
                        onClick={() =>
                          setOpen(open === kind.documentType ? null : kind.documentType)
                        }
                      >
                        {kind.totalCount}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{kind.documentCount}</TableCell>
                    <TableCell>{when(kind.lastPrintedAt)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: 'none' }}>
                      <Collapse in={open === kind.documentType} unmountOnExit>
                        <WhoPrintedIt documentType={kind.documentType} from={from} to={to} />
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
